/* Logicae motion — choreography primitives.
 * stagger / scene / rail / opening, driven by native scroll only.
 * Contract: motion is garnish — every page is complete without it.
 * The pre-paint <head> snippet sets html.motion (JS present, motion OK);
 * this file adds html.motion-ready to play openings and wires the rest.
 * Rails: swipe mode (overflow-x) is the CSS default; this file opts a rail
 * into pinned-scrub mode by adding .m-rail-live once it takes ownership. */
(function () {
    'use strict';

    var reduced = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return; /* CSS force-final block owns this case */
    if (!('IntersectionObserver' in window)) return;

    var docEl = document.documentElement;
    var CAP = 12;          /* stagger delay ceiling */
    var SETTLE = 1600;     /* ms after reveal before transitions hand back */

    /* ================= opening ================= */
    function armOpening() {
        var done = false;
        function fire() {
            if (done) return;
            done = true;
            docEl.classList.add('motion-ready');
        }
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(fire);
        }
        setTimeout(fire, 400); /* fonts must never hold the page hostage */
    }

    /* ================= stagger ================= */
    function settle(el) {
        clearTimeout(el.__mSettle);
        el.__mSettle = setTimeout(function () {
            el.classList.add('settled');
        }, SETTLE);
    }

    var staggerIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
                entries[i].target.classList.add('in');
                settle(entries[i].target);
                staggerIO.unobserve(entries[i].target);
            }
        }
    }, { rootMargin: '0px 0px -8% 0px' });

    function markChildren(el) {
        var kids = el.children;
        for (var i = 0; i < kids.length; i++) {
            kids[i].style.setProperty('--mi', String(Math.min(i, CAP)));
        }
    }

    function initStagger(el) {
        markChildren(el);
        staggerIO.observe(el);
        /* infinite scroll: batches appended after .in animate as a fresh
           cascade — held at initial state one frame, then released */
        if (!window.MutationObserver) return;
        new MutationObserver(function (muts) {
            var added = [], idx = 0, i, j;
            for (i = 0; i < muts.length; i++) {
                for (j = 0; j < muts[i].addedNodes.length; j++) {
                    var n = muts[i].addedNodes[j];
                    if (n.nodeType !== 1) continue;
                    n.style.setProperty('--mi', String(Math.min(idx++, CAP)));
                    n.classList.add('m-hold');
                    added.push(n);
                }
            }
            if (!added.length) return;
            el.classList.remove('settled');
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    for (var k = 0; k < added.length; k++) {
                        added[k].classList.remove('m-hold');
                    }
                    settle(el);
                });
            });
        }).observe(el, { childList: true });
    }

    /* ================= scene / rail progress ================= */
    var scenes = [];
    var ticking = false;

    function update() {
        ticking = false;
        var vh = window.innerHeight;
        for (var i = 0; i < scenes.length; i++) {
            var s = scenes[i];
            if (!s.live) continue;
            var r = s.el.getBoundingClientRect();
            var p;
            if (s.rail) {
                /* pinned rail: progress across the pin distance, offset by
                   the sticky top (the stage locks centered in the viewport) */
                var span = r.height - s.stageH;
                p = span > 0 ? (s.railTop - r.top) / span : 1;
            } else {
                /* generic scene: progress across full visibility traversal */
                p = (vh - r.top) / (vh + r.height);
            }
            p = p < 0 ? 0 : p > 1 ? 1 : p;
            s.el.style.setProperty('--p', p.toFixed(4));
        }
    }

    function onScroll() {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }

    function watch(scene) {
        scenes.push(scene);
        new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                scene.live = entries[i].isIntersecting;
            }
            if (scene.live) onScroll();
        }, { rootMargin: '40px' }).observe(scene.el);
        if (scenes.length === 1) {
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll);
        }
    }

    function initScene(el) {
        watch({ el: el, live: false, rail: false, stageH: 0 });
    }

    /* ================= rail ================= */
    function initRail(el) {
        var stage = el.querySelector('.m-rail-stage');
        var track = el.querySelector('.m-rail-track');
        if (!stage || !track) return;
        var scene = { el: el, live: false, rail: true, stageH: 0, railTop: 0 };

        function measure() {
            if (window.innerWidth < 600) {
                /* hand back to the CSS-default swipe mode */
                el.classList.remove('m-rail-live');
                el.style.height = '';
                el.style.removeProperty('--rail-max');
                el.style.removeProperty('--rail-top');
                return;
            }
            el.classList.add('m-rail-live');
            var overflow = track.scrollWidth - stage.clientWidth;
            if (overflow < 0) overflow = 0;
            scene.stageH = stage.offsetHeight;
            /* lock the stage centered in the viewport, not at the top */
            scene.railTop = Math.max(0,
                Math.round((window.innerHeight - scene.stageH) / 2));
            el.style.setProperty('--rail-top', scene.railTop + 'px');
            el.style.setProperty('--rail-max', overflow + 'px');
            el.style.height = (scene.stageH + overflow) + 'px';
        }

        function remeasure() {
            clearTimeout(el.__mRs);
            el.__mRs = setTimeout(function () {
                measure();
                onScroll();
            }, 120);
        }

        measure();
        watch(scene);
        /* the track grows as its images load — keep the math honest */
        if (window.ResizeObserver) {
            new ResizeObserver(remeasure).observe(track);
        } else {
            var imgs = track.querySelectorAll('img');
            for (var i = 0; i < imgs.length; i++) {
                imgs[i].addEventListener('load', remeasure);
            }
        }
        window.addEventListener('resize', remeasure);
    }

    /* ================= boot ================= */
    function boot() {
        armOpening();
        var els = document.querySelectorAll('[data-motion]');
        for (var i = 0; i < els.length; i++) {
            var kind = els[i].getAttribute('data-motion');
            if (kind === 'stagger') initStagger(els[i]);
            else if (kind === 'scene') initScene(els[i]);
            else if (kind === 'rail') initRail(els[i]);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
